import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, IconButton, Badge, Popover, Typography, Divider,
    Accordion, AccordionSummary, AccordionDetails, Button, Collapse
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RemoveIconButton from './common/RemoveIconButton';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { AnimationTiming } from '../constants/enums';
import config from '../config/config';

const NotificationCenter = () => {
    const { api, currentUser, token } = useAuth();
    const { showToast } = useToast();
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [deletingIds, setDeletingIds] = useState(new Set());
    const [unreadCount, setUnreadCount] = useState(0);
    const [expanded, setExpanded] = useState(false);

    const fetchNotifications = useCallback(async () => {
        if (!token) return;
        try {
            const response = await api.get('/notifications/');
            setNotifications(response.data);
            const countRes = await api.get('/notifications/unread-count');
            setUnreadCount(countRes.data.count);
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        }
    }, [api, token]);

    const fetchNotificationsRef = useRef(fetchNotifications);
    useEffect(() => {
        fetchNotificationsRef.current = fetchNotifications;
    }, [fetchNotifications]);

    useEffect(() => {
        if (!token) return;

        let socket = null;
        let reconnectTimeout = null;
        let isMounted = true;

        const connectWebSocket = () => {
            if (!isMounted) return;

            const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
            const baseUrl = config.API_URL.replace(/^http(s)?/, protocol);
            const wsUrl = `${baseUrl}/notifications/ws?token=${token}`;

            socket = new WebSocket(wsUrl);

            socket.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'NEW_NOTIFICATION' || data.type === 'COUNT_UPDATE' || data.type === 'MEMBERS_UPDATED' || data.type === 'REMOVED_FROM_LEDGER') {
                    if (data.type === 'MEMBERS_UPDATED') {
                        // Dispatch event for shared users list update
                        window.dispatchEvent(new CustomEvent('ledgerMembersChanged', {
                            detail: { ledgerId: data.ledger_id }
                        }));
                    } else if (data.type === 'REMOVED_FROM_LEDGER') {
                        // Show toast and refresh dashboard
                        showToast(`You are removed from "${data.ledger_name}"`, 'success');
                        window.dispatchEvent(new CustomEvent('ledgerChanged'));
                    } else {
                        setUnreadCount(data.unread_count);
                        if (data.type === 'NEW_NOTIFICATION') {
                            // Refresh the full list to show the new notification
                            if (fetchNotificationsRef.current) fetchNotificationsRef.current();
                        }
                    }
                }
            };

            socket.onclose = () => {
                if (isMounted) {
                    // Reconnect after 5 seconds
                    reconnectTimeout = setTimeout(connectWebSocket, 5000);
                }
            };

            socket.onerror = (error) => {
                if (isMounted) {
                    console.error('WebSocket error:', error);
                }
                if (socket.readyState === WebSocket.OPEN) {
                    socket.close();
                }
            };
        };

        fetchNotifications();
        connectWebSocket();

        return () => {
            isMounted = false;
            if (socket) {
                if (socket.readyState === WebSocket.CONNECTING) {
                    // Defer closing until it connects to avoid the browser throwing a "closed before established" error
                    socket.onopen = () => socket.close();
                } else if (socket.readyState === WebSocket.OPEN) {
                    socket.close();
                }
            }
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
        };
    }, [token, fetchNotifications, showToast]);


    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
        fetchNotifications();
    };

    const handleClose = () => {
        setAnchorEl(null);
        setExpanded(false);
    };

    const handleMarkAsRead = async (id) => {
        // Optimistic update
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));

        try {
            await api.patch(`/notifications/${id}`);
        } catch (error) {
            console.error('Failed to mark as read', error);
            // Sync back on error
            fetchNotifications();
        }
    };

    const handleDelete = async (event, id) => {
        event.stopPropagation();
        setDeletingIds(prev => new Set([...prev, id]));
        const wasUnread = !notifications.find(n => n.id === id)?.is_read;
        if (wasUnread) {
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
        try {
            await api.delete(`/notifications/${id}`);
        } catch (error) {
            console.error('Failed to delete notification', error);
            fetchNotifications();
        } finally {
            setTimeout(() => {
                setNotifications(prev => prev.filter(n => n.id !== id));
                setDeletingIds(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }, AnimationTiming.RECORD_REMOVE_MS);
        }
    };

    const handleAccordionChange = (panelId, isRead) => (event, isExpanded) => {
        setExpanded(isExpanded ? panelId : false);
        if (isExpanded && !isRead) {
            handleMarkAsRead(panelId);
        }
    };

    const renderSafeText = (rawText) => {
        if (!rawText) return null;
        const parts = rawText.split(/(<b>.*?<\/b>)/g);
        return parts.map((part, i) => {
            if (part.startsWith('<b>') && part.endsWith('</b>')) {
                return <strong key={i}>{part.slice(3, -4)}</strong>;
            }
            return part;
        });
    };

    const renderContent = (notification) => {
        const isExpired = notification.expires_at && new Date(notification.expires_at) < new Date();
        const content = isExpired ? notification.expired_content : notification.content;

        if (!content) return null;

        return (
            <Box>
                {content.text && (
                    <Typography
                        variant="body2"
                        sx={{ mb: content.buttons?.length > 0 ? 2 : 0, color: 'text.secondary', lineHeight: 1.6 }}
                    >
                        {renderSafeText(content.text)}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {content.buttons?.map((btn, idx) => (
                        <Button
                            key={idx}
                            variant="contained"
                            size="small"
                            onClick={async () => {
                                try {
                                    const action = btn.label.toLowerCase(); // 'accept' or 'decline'
                                    await api.post(btn.action, { action });
                                    fetchNotifications();
                                    // Dispatch custom event to refresh ledgers in other components
                                    window.dispatchEvent(new CustomEvent('ledgerChanged', { detail: { action } }));
                                } catch (error) {
                                    console.error('Failed to respond to invitation', error);
                                }
                            }}
                            disabled={isExpired}
                            sx={{ borderRadius: 1 }}
                        >
                            {btn.label}
                        </Button>
                    ))}
                </Box>
                {isExpired && !notification.expired_content && (
                    <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
                        This action has expired.
                    </Typography>
                )}
            </Box>
        );
    };

    const open = Boolean(anchorEl);

    if (!currentUser) return null;

    return (
        <>
            <IconButton color="inherit" onClick={handleOpen} sx={{ mr: 1 }}>
                <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}>
                    <NotificationsIcon sx={{ color: 'text.secondary' }} />
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: { xs: 'calc(100vw - 32px)', sm: 400 },
                        maxWidth: 400,
                        maxHeight: 500,
                        mt: 1.5,
                        boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notifications</Typography>
                    {unreadCount > 0 && (
                        <Badge badgeContent={unreadCount} color="error" sx={{ mr: 2 }} />
                    )}
                </Box>
                <Divider />
                <Box sx={{ overflowY: 'auto', maxHeight: 400, bgcolor: '#F9FAFB' }}>
                    {notifications.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <NotificationsIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                            <Typography color="text.secondary">All caught up!</Typography>
                        </Box>
                    ) : (
                        notifications.map((n) => {
                            const isDeleting = deletingIds.has(n.id);
                            return (
                                <Collapse
                                    key={n.id}
                                    in={!isDeleting}
                                    timeout={AnimationTiming.RECORD_REMOVE_MS}
                                    unmountOnExit={false}
                                >
                                    <Accordion
                                        expanded={expanded === n.id}
                                        onChange={handleAccordionChange(n.id, n.is_read)}
                                        elevation={0}
                                        sx={{
                                            bgcolor: isDeleting ? 'action.hover' : 'transparent',
                                            opacity: isDeleting ? 0 : 1,
                                            transform: isDeleting ? 'scaleY(0.8)' : 'scaleY(1)',
                                            pointerEvents: isDeleting ? 'none' : 'auto',
                                            transition: `all ${AnimationTiming.RECORD_REMOVE_MS}ms ease`,
                                            '&:before': { display: 'none' },
                                            borderBottom: '1px solid #E5E7EB',
                                            '&.Mui-expanded': { margin: 0 }
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                                            sx={{
                                                px: 2,
                                                '& .MuiAccordionSummary-content': { alignItems: 'center', my: 1.5, minWidth: 0 }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                                                {!n.is_read && (
                                                    <FiberManualRecordIcon
                                                        sx={{ color: 'error.main', fontSize: 10, mr: 1.5, flexShrink: 0 }}
                                                    />
                                                )}
                                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: n.is_read ? 500 : 700,
                                                            color: 'text.primary',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {n.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(n.created_at).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <RemoveIconButton
                                                size="small"
                                                onClick={(e) => handleDelete(e, n.id)}
                                                disabled={isDeleting}
                                                title="Delete"
                                                stopPropagation
                                                sx={{ ml: 1 }}
                                            />
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0, bgcolor: 'white' }}>
                                            {renderContent(n)}
                                        </AccordionDetails>
                                    </Accordion>
                                </Collapse>
                            );
                        })
                    )}
                </Box>
                <Divider />
                <Button fullWidth sx={{ py: 1.5, borderRadius: '0 0 12px 12px', color: 'text.secondary', fontSize: '0.75rem' }} onClick={handleClose}>
                    Close
                </Button>
            </Popover>
        </>
    );
};

export default NotificationCenter;
